import { createTheme } from '@mui/material'

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    alert: true
  }
}
declare module '@mui/material/styles' {
  interface TypeText {
    title: string
  }
  interface Palette {
    neutral: {
      grey2: string
      grey3: string
      grey4: string
      grey6: string
      grey7: string
      grey8: string
      grey9: string
      grey10: string
      grey11: string
      grey12: string
    }
    icon: {
      main: string
      hover: string
      selected: string
    }
    brand: {
      main: string
      secondary: string
      light: string
      blue: string
    }
    commonColors: {
      white: string
      black: string
      darkGrey: string
    }
  }
}

const palette = {
  primary: {
    main: '#4861ff',
    dark: '#2740DD',
  },
  secondary: {
    main: '#F3F3F5',
  },
  icon: {
    main: '#7B7794',
    hover: '#140D40',
    selected: '#4861FF',
  },
  action: {
    hover: '#F9F9F9',
    selected: '#F1F3FF',
    focus: '#D9D8E0',
    disabled: '#CCCCCC',
  },
  text: {
    primary: '#1C1C1C',
    secondary: '#9E9E9E',
    title: '#140D40',
    disabled: '#CCCCCC',
  },
  neutral: {
    grey2: '#424242',
    grey3: '#5C5C5C',
    grey4: '#757575',
    grey6: '#B2B2B2',
    grey7: '#CCCCCC',
    grey8: '#E5E5E5',
    grey9: '#F5F5F5',
    grey10: '#999999',
    grey11: '#333333',
    grey12: '#FAFAFA',
  },
  brand: {
    main: '#4861FF',
    secondary: '#EE1952',
    light: '#C7C8BF',
    blue: '#3b99fc',
  },
  success: {
    main: '#4FB853',
  },
  error: {
    main: '#D0021B',
    light: '#fd6a7c',
  },
  warning: {
    main: '#FFC400',
  },
  commonColors: {
    white: '#FFFFFF',
    black: '#000000',
    darkGrey: '#191919',
  },
}

const buttonProps = {
  color: 'white',
  height: '38.5px',
  fontSize: '12px',
  borderRadius: '19.5px',
  minWidth: '78px',
  padding: '2px 48px 2.5px 48px',
  textTransform: 'none' as const,
  border: 'transparent',
}

const theme = {
  typography: {
    fontFamily: 'Arial',
    body1: { fontSize: '12px' },
    body2: { fontSize: '12px', fontWeight: 'bold' },
    button: {
      fontSize: '10px',
      fontWeight: 'bold',
      color: palette.primary.main,
    },
    h1: {
      fontSize: '32px',
      fontFamily: 'Arial',
      color: palette.text.title,
      letterSpacing: '-1px',
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '18px',
      color: palette.text.primary,
      margin: 0,
    },
  },
  spacing: 8,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 800,
      lg: 1000,
      xl: 1296,
    },
  },
  palette,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Arial',
          fontSize: '12px',
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
          color: palette.text.primary,
          '.leaflet-marker-icon, .leaflet-marker-shadow': {
            display: 'none',
            '&.store_marker': {
              display: 'block !important',
            },
            '&.driver_marker': {
              display: 'block',
            },
          },
          '*': { boxSizing: 'border-box' },
        },
        button: {
          backgroundColor: 'transparent',
          border: 'none',
          margin: 0,
          padding: 0,
          cursor: 'pointer',
        },
        '.leaflet-pane .leaflet-tile-pane': {
          filter: 'grayscale(80%)',
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fill: palette.icon.main,
          '&:hover': {
            backgroundColor: 'transparent',
            fill: palette.icon.hover,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained' as const,
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'alert' as const },
          style: {
            backgroundColor: palette.error.main,
            '&:hover': {
              backgroundColor: palette.error.light,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
            ...buttonProps,
          },
        },
      ],
      styleOverrides: {
        containedPrimary: {
          backgroundColor: palette.primary.main,
          ...buttonProps,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: palette.action.hover,
          fontSize: '18px',
          '&:before': {
            border: '1px solid transparent',
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            WebkitTextFillColor: palette.text.primary,
          },
          focused: {
            underline: {
              borderBottom: palette.primary.main,
            },
          },
        },

        input: {
          paddingTop: '20px',
          paddingBottom: '2px',
          '&.Mui-disabled': {
            color: palette.text.primary,
            WebkitTextFillColor: palette.text.primary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          top: '-5px',
          left: '-4px',
          transform: 'translate(14px, -11px)',
        },
        filled: {
          color: palette.text.secondary,
          fontSize: '12px',
          fontWeight: '700',
          margin: 0,
          focused: {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiDrawer: { styleOverrides: { paper: { paddingTop: '16px' } } },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& fieldset': {
            top: 0,
            border: 'none',
            color: palette.text.secondary,
            borderRadius: '5.5px',
          },
          '&.Mui-focused fieldset': {
            borderBottom: '1px solid',
            borderColor: palette.primary.main,
          },
          '&:hover': {
            cursor: 'pointer',
          },
        },
        outlined: {
          paddingTop: '10px',
          paddingBottom: '10px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: 'Arial-BoldMT',
          borderRadius: '3px',
          padding: '4px',
          backgroundColor: '#140d40',
        },
        arrow: { color: '#140d40' },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          button: {
            color: palette.text.secondary,
          },
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        borderColor: 'black',
        borderBottom: '20px',
        minHeight: '20px',
        maxWidth: '140px',
      },
    },
  },
  MuiUseMediaQuery: {
    defaultProps: {
      noSsr: true,
    },
  },
}

export const routePlanningColors = [
  '#a661ff',
  '#d98a94',
  '#0acc00',
  '#008091',
  '#00abc7',
  '#ffba03',
  '#801c3d',
  '#e8713b',
  '#e8dd80',
  '#5363ce',
  '#5ece80',
  '#e60088',
  '#4fb853',
  '#ff6e40',
  '#aa00ff',
  '#60f066',
  '#ea80fc',
  '#c6ff00',
  '#304ffe',
  '#ffd740',
  '#a7ffeb',
  '#d50000',
  '#7c4dff',
  '#80d8ff',
  '#f4ff81',
  '#3d54fe',
  '#1de9b6',
  '#ff3d00',
  '#b388ff',
  '#ffea00',
  '#b9f6ca',
]

export const slotColors = {
  full: '#4861ff',
  almostFull: '#6d81ff',
  halfway: '#edefff',
  empty: '#FFFFFF',
  inThePast: {
    full: '#5c5c5c',
    almostFull: '#7d7d7d',
    halfway: '#dedede',
    empty: '#FFFFFF',
  },
}

export default createTheme(theme)
